/**
 * Calls the Worker `POST /auth/bootstrap` with the Firebase ID token after a
 * successful sign-in / sign-up. Ensures the user's profile exists and applies
 * the admin custom claim for allow-listed emails.
 *
 * The response is intentionally minimal (`{ profileCreated, isAdmin }`); errors
 * are opaque by design (the Worker never leaks internals). We surface a generic
 * failure to the caller and let the UI decide whether to retry.
 */

export interface BootstrapResponse {
  profileCreated: boolean;
  isAdmin: boolean;
}

export interface BootstrapClientOptions {
  /** Worker API base URL (no trailing slash needed). */
  workerUrl: string;
  /** Injectable fetch for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/**
 * POST the ID token to the Worker. Returns the parsed result on 200, or `null`
 * if the call did not succeed (bootstrap is idempotent so a later retry is safe).
 */
export async function bootstrapProfile(
  idToken: string,
  opts: BootstrapClientOptions,
): Promise<BootstrapResponse | null> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const base = opts.workerUrl.replace(/\/+$/, "");

  let res: Response;
  try {
    res = await fetchImpl(`${base}/auth/bootstrap`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;

  try {
    const json = (await res.json()) as Partial<BootstrapResponse>;
    return {
      profileCreated: json.profileCreated === true,
      isAdmin: json.isAdmin === true,
    };
  } catch {
    return null;
  }
}
